import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Megaphone, RefreshCcw, Trash2 } from 'lucide-react';
import api from '@/lib/axios';

const TABS = [['anunciantes', 'Anunciantes'], ['posicoes', 'Posições'], ['campanhas', 'Campanhas'],
  ['config', 'Frequência inline'], ['pedidos', 'Pedidos de proposta']];
const input = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs';
const btn = 'rounded-lg px-3 py-1.5 text-xs font-bold';
const advVazio = { nome: '', nome_comercial: '', email: '', telefone: '', website: '', categoria: 'oficina', nif: '', responsavel: '', observacoes: '', ativo: true };
const campVazia = {
  advertiser_id: '', nome_interno: '', tipo: 'direct', posicao: '', imagem_desktop: '', imagem_mobile: '',
  texto: '', botao: '', link: '', pagina: '', categoria: '', distrito: '', inicio: '', fim: '',
  prioridade: 5, preco: 0, taxa_iva: 23, limite_impressoes: 0, limite_cliques: 0,
  frequencia_utilizador: 0, desktop: true, tablet: true, mobile: true,
};

const Campo = ({ label, children }) => (
  <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">
    {label}
    {children}
  </label>
);

export default function MarketplaceAdsAdminPanel() {
  const [tab, setTab] = useState('anunciantes');
  const [advertisers, setAdvertisers] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [positions, setPositions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [contadores, setContadores] = useState({});
  const [leads, setLeads] = useState([]);
  const [cfg, setCfg] = useState({ insert_every_n_items: 10, primeira_posicao: 4, max_por_pagina: 3, desktop: true, tablet: true, mobile: true });
  const [adv, setAdv] = useState(advVazio);
  const [advEdit, setAdvEdit] = useState(null);
  const [camp, setCamp] = useState(campVazia);
  const [campEdit, setCampEdit] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const base = '/admin/marketplace/publicidade';

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [a, p, c, cf, l] = await Promise.all([
        api.get(`${base}/anunciantes`), api.get(`${base}/posicoes`), api.get(`${base}/campanhas`),
        api.get(`${base}/config`), api.get(`${base}/pedidos`),
      ]);
      setAdvertisers(a.data.items || []); setCategorias(a.data.categorias || []);
      setPositions(p.data.items || []);
      setCampaigns(c.data.items || []); setContadores(c.data.contadores || {});
      if (cf.data.inline) setCfg(cf.data.inline);
      setLeads(l.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Falha a carregar a publicidade.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const acao = async (fn, sucesso) => {
    setBusy(true); setErr(''); setMsg('');
    try { await fn(); setMsg(sucesso); await load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === 'string' ? d : JSON.stringify(d || 'Ação não aplicada.'));
    } finally { setBusy(false); }
  };

  const guardarAnunciante = (ev) => {
    ev.preventDefault();
    return acao(() => (advEdit ? api.put(`${base}/anunciantes/${advEdit}`, adv) : api.post(`${base}/anunciantes`, adv)),
      advEdit ? 'Anunciante atualizado.' : 'Anunciante criado.')
      .then(() => { setAdv(advVazio); setAdvEdit(null); });
  };

  const guardarCampanha = (ev) => {
    ev.preventDefault();
    const body = {
      ...camp,
      prioridade: Number(camp.prioridade) || 5, preco: Number(camp.preco) || 0,
      taxa_iva: Number(camp.taxa_iva) || 0, limite_impressoes: Number(camp.limite_impressoes) || 0,
      limite_cliques: Number(camp.limite_cliques) || 0,
      frequencia_utilizador: Number(camp.frequencia_utilizador) || 0,
      inicio: camp.inicio ? new Date(camp.inicio).toISOString() : '',
      fim: camp.fim ? new Date(camp.fim).toISOString() : '',
    };
    return acao(() => (campEdit ? api.put(`${base}/campanhas/${campEdit}`, body) : api.post(`${base}/campanhas`, body)),
      campEdit ? 'Campanha atualizada.' : 'Campanha criada em rascunho.')
      .then(() => { setCamp(campVazia); setCampEdit(null); });
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-ads-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
            <Megaphone className="h-4 w-4 text-emerald-600" /> Publicidade direta
          </h2>
          <p className="text-xs text-slate-500">
            Anunciantes, posições, campanhas e frequência entre resultados. Só publicidade direta está ativa —
            nenhum script Google é carregado.
          </p>
        </div>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600" data-testid="admin-ads-reload">
          <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`${btn} ${tab === key ? 'bg-emerald-600 text-white' : 'border border-slate-300 text-slate-600'}`}
            data-testid={`admin-ads-tab-${key}`}>{label}</button>
        ))}
      </div>

      {err && <p className="mt-2 text-xs font-semibold text-red-600" data-testid="admin-ads-error">{err}</p>}
      {msg && <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid="admin-ads-msg">{msg}</p>}
      {loading && <p className="mt-3 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>}

      {tab === 'anunciantes' && (
        <div className="mt-3" data-testid="admin-ads-advertisers">
          <form onSubmit={guardarAnunciante} className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-3">
            <input className={input} placeholder="Nome" value={adv.nome} onChange={(e) => setAdv({ ...adv, nome: e.target.value })} required data-testid="adv-name" />
            <input className={input} placeholder="Nome comercial" value={adv.nome_comercial} onChange={(e) => setAdv({ ...adv, nome_comercial: e.target.value })} data-testid="adv-trade" />
            <input className={input} type="email" placeholder="Email" value={adv.email} onChange={(e) => setAdv({ ...adv, email: e.target.value })} required data-testid="adv-email" />
            <input className={input} placeholder="Telefone" value={adv.telefone} onChange={(e) => setAdv({ ...adv, telefone: e.target.value })} data-testid="adv-phone" />
            <input className={input} placeholder="Website (https://)" value={adv.website} onChange={(e) => setAdv({ ...adv, website: e.target.value })} data-testid="adv-site" />
            <select className={input} value={adv.categoria} onChange={(e) => setAdv({ ...adv, categoria: e.target.value })} data-testid="adv-category">
              {(categorias.length ? categorias : ['oficina']).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input className={input} placeholder="NIF (faturação, nunca público)" value={adv.nif} onChange={(e) => setAdv({ ...adv, nif: e.target.value })} data-testid="adv-nif" />
            <input className={input} placeholder="Responsável" value={adv.responsavel} onChange={(e) => setAdv({ ...adv, responsavel: e.target.value })} data-testid="adv-manager" />
            <input className={input} placeholder="Observações" value={adv.observacoes} onChange={(e) => setAdv({ ...adv, observacoes: e.target.value })} data-testid="adv-notes" />
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
              <input type="checkbox" checked={adv.ativo} onChange={(e) => setAdv({ ...adv, ativo: e.target.checked })} data-testid="adv-active" /> Ativo
            </label>
            <div className="flex gap-2 sm:col-span-2">
              <button type="submit" disabled={busy} className={`${btn} bg-emerald-600 text-white disabled:opacity-60`} data-testid="adv-save">
                {advEdit ? 'Guardar anunciante' : 'Criar anunciante'}
              </button>
              {advEdit && <button type="button" onClick={() => { setAdvEdit(null); setAdv(advVazio); }} className={`${btn} border border-slate-300 text-slate-600`} data-testid="adv-cancel">Cancelar</button>}
            </div>
          </form>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-xs">
              <thead className="text-[10px] uppercase text-slate-400">
                <tr><th className="py-2">Anunciante</th><th>Categoria</th><th>Contactos</th><th>Campanhas</th><th>Valor</th><th>Estado</th><th /></tr>
              </thead>
              <tbody>
                {advertisers.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`adv-row-${a.id}`}>
                    <td className="py-2">
                      <p className="font-semibold text-slate-800">{a.nome_comercial || a.nome}</p>
                      <p className="text-[10px] text-slate-400">{a.responsavel || '—'}</p>
                    </td>
                    <td>{a.categoria}</td>
                    <td className="text-[10px] text-slate-600">{a.email}<br />{a.telefone || '—'}</td>
                    <td>{a.campanhas}</td>
                    <td>{Number(a.valor_total || 0).toFixed(2)} €</td>
                    <td>{a.ativo ? 'Ativo' : 'Inativo'}</td>
                    <td>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => { setAdvEdit(a.id); setAdv({ ...advVazio, ...a }); }} className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`adv-edit-${a.id}`}>Editar</button>
                        <button type="button" disabled={busy} onClick={() => acao(() => api.delete(`${base}/anunciantes/${a.id}`), 'Anunciante apagado.')} className="rounded border border-rose-300 px-1.5 py-0.5 text-rose-700" data-testid={`adv-delete-${a.id}`}><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!advertisers.length && !loading && (
                  <tr><td colSpan={7} className="py-4 text-slate-500" data-testid="adv-empty">Sem anunciantes registados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'posicoes' && (
        <div className="mt-3" data-testid="admin-ads-positions">
          <button type="button" disabled={busy} onClick={() => acao(() => api.post(`${base}/posicoes/seed`), 'Posições criadas.')}
            className={`${btn} border border-slate-300 text-slate-700`} data-testid="pos-seed">
            Criar posições em falta (inativas)
          </button>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-xs">
              <thead className="text-[10px] uppercase text-slate-400">
                <tr><th className="py-2">Posição</th><th>Formato</th><th>Dimensões</th><th>Dispositivos</th><th>Preço s/IVA</th><th>Dias</th><th>Máx.</th><th>Rotação</th><th>Ativa</th><th>Campanhas</th></tr>
              </thead>
              <tbody>
                {positions.map((p) => (
                  <tr key={p.code} className="border-t border-slate-100" data-testid={`pos-row-${p.code}`}>
                    <td className="py-2">
                      <p className="font-semibold text-slate-800">{p.label}</p>
                      <p className="text-[10px] text-slate-400">{p.code}</p>
                    </td>
                    <td>{p.formato}</td>
                    <td>
                      <input defaultValue={p.dimensoes || ''} onBlur={(e) => acao(() => api.put(`${base}/posicoes/${p.code}`, { dimensoes: e.target.value }), 'Posição atualizada.')}
                        className="w-20 rounded border border-slate-300 px-1 py-0.5" data-testid={`pos-dim-${p.code}`} />
                    </td>
                    <td className="text-[10px]">
                      {['desktop', 'tablet', 'mobile'].map((d) => (
                        <label key={d} className="mr-1 inline-flex items-center gap-0.5">
                          <input type="checkbox" defaultChecked={p[d] !== false}
                            onChange={(e) => acao(() => api.put(`${base}/posicoes/${p.code}`, { [d]: e.target.checked }), 'Posição atualizada.')}
                            data-testid={`pos-${d}-${p.code}`} />{d.slice(0, 3)}
                        </label>
                      ))}
                    </td>
                    <td>
                      <input type="number" step="0.01" min="0" defaultValue={p.preco ?? ''} placeholder="—"
                        onBlur={(e) => e.target.value !== '' && acao(() => api.put(`${base}/posicoes/${p.code}`, { preco: Number(e.target.value) }), 'Preço atualizado.')}
                        className="w-20 rounded border border-slate-300 px-1 py-0.5" data-testid={`pos-price-${p.code}`} />
                    </td>
                    <td>
                      <input type="number" min="1" defaultValue={p.duracao_dias || 30}
                        onBlur={(e) => acao(() => api.put(`${base}/posicoes/${p.code}`, { duracao_dias: Number(e.target.value) }), 'Duração atualizada.')}
                        className="w-14 rounded border border-slate-300 px-1 py-0.5" data-testid={`pos-days-${p.code}`} />
                    </td>
                    <td>
                      <input type="number" min="1" max="20" defaultValue={p.max_campanhas || 3}
                        onBlur={(e) => acao(() => api.put(`${base}/posicoes/${p.code}`, { max_campanhas: Number(e.target.value) }), 'Limite atualizado.')}
                        className="w-14 rounded border border-slate-300 px-1 py-0.5" data-testid={`pos-max-${p.code}`} />
                    </td>
                    <td>
                      <select defaultValue={p.rotacao || 'prioridade'} onChange={(e) => acao(() => api.put(`${base}/posicoes/${p.code}`, { rotacao: e.target.value }), 'Rotação atualizada.')}
                        className="rounded border border-slate-300 px-1 py-0.5" data-testid={`pos-rot-${p.code}`}>
                        <option value="prioridade">prioridade</option>
                        <option value="aleatoria">aleatória</option>
                        <option value="sequencial">sequencial</option>
                      </select>
                    </td>
                    <td>
                      <input type="checkbox" checked={!!p.ativo}
                        onChange={(e) => acao(() => api.put(`${base}/posicoes/${p.code}`, { ativo: e.target.checked }), 'Posição atualizada.')}
                        data-testid={`pos-active-${p.code}`} />
                    </td>
                    <td>{p.campanhas_ativas}</td>
                  </tr>
                ))}
                {!positions.length && !loading && (
                  <tr><td colSpan={10} className="py-4 text-slate-500" data-testid="pos-empty">Sem posições. Use o botão acima.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'campanhas' && (
        <div className="mt-3" data-testid="admin-ads-campaigns">
          <form onSubmit={guardarCampanha} className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-3">
            <Campo label="Anunciante">
              <select className={input} value={camp.advertiser_id} onChange={(e) => setCamp({ ...camp, advertiser_id: e.target.value })} required data-testid="camp-advertiser">
                <option value="">Escolher...</option>
                {advertisers.map((a) => <option key={a.id} value={a.id}>{a.nome_comercial || a.nome}</option>)}
              </select>
            </Campo>
            <Campo label="Nome interno">
              <input className={input} value={camp.nome_interno} onChange={(e) => setCamp({ ...camp, nome_interno: e.target.value })} required data-testid="camp-name" />
            </Campo>
            <Campo label="Posição">
              <select className={input} value={camp.posicao} onChange={(e) => setCamp({ ...camp, posicao: e.target.value })} required data-testid="camp-position">
                <option value="">Escolher...</option>
                {positions.map((p) => <option key={p.code} value={p.code}>{p.label}{p.ativo ? '' : ' (inativa)'}</option>)}
              </select>
            </Campo>
            <Campo label="Imagem desktop (https .png/.jpg)">
              <input className={input} value={camp.imagem_desktop} onChange={(e) => setCamp({ ...camp, imagem_desktop: e.target.value })} required data-testid="camp-image" />
            </Campo>
            <Campo label="Imagem mobile (opcional)">
              <input className={input} value={camp.imagem_mobile} onChange={(e) => setCamp({ ...camp, imagem_mobile: e.target.value })} data-testid="camp-image-mobile" />
            </Campo>
            <Campo label="Link de destino (https)">
              <input className={input} value={camp.link} onChange={(e) => setCamp({ ...camp, link: e.target.value })} required data-testid="camp-link" />
            </Campo>
            <Campo label="Texto">
              <input className={input} value={camp.texto} onChange={(e) => setCamp({ ...camp, texto: e.target.value })} data-testid="camp-text" />
            </Campo>
            <Campo label="Botão">
              <input className={input} value={camp.botao} onChange={(e) => setCamp({ ...camp, botao: e.target.value })} data-testid="camp-button" />
            </Campo>
            <Campo label="Página (opcional)">
              <input className={input} value={camp.pagina} onChange={(e) => setCamp({ ...camp, pagina: e.target.value })} data-testid="camp-page" />
            </Campo>
            <Campo label="Distrito (opcional)">
              <input className={input} value={camp.distrito} onChange={(e) => setCamp({ ...camp, distrito: e.target.value })} data-testid="camp-district" />
            </Campo>
            <Campo label="Categoria (opcional)">
              <input className={input} value={camp.categoria} onChange={(e) => setCamp({ ...camp, categoria: e.target.value })} data-testid="camp-category" />
            </Campo>
            <Campo label="Prioridade (1-10)">
              <input className={input} type="number" min="1" max="10" value={camp.prioridade} onChange={(e) => setCamp({ ...camp, prioridade: e.target.value })} data-testid="camp-priority" />
            </Campo>
            <Campo label="Início">
              <input className={input} type="datetime-local" value={camp.inicio} onChange={(e) => setCamp({ ...camp, inicio: e.target.value })} required data-testid="camp-start" />
            </Campo>
            <Campo label="Fim">
              <input className={input} type="datetime-local" value={camp.fim} onChange={(e) => setCamp({ ...camp, fim: e.target.value })} required data-testid="camp-end" />
            </Campo>
            <Campo label="Preço s/IVA (€)">
              <input className={input} type="number" min="0" step="0.01" value={camp.preco} onChange={(e) => setCamp({ ...camp, preco: e.target.value })} data-testid="camp-price" />
            </Campo>
            <Campo label="Limite de impressões (0 = sem limite)">
              <input className={input} type="number" min="0" value={camp.limite_impressoes} onChange={(e) => setCamp({ ...camp, limite_impressoes: e.target.value })} data-testid="camp-max-impressions" />
            </Campo>
            <Campo label="Limite de cliques (0 = sem limite)">
              <input className={input} type="number" min="0" value={camp.limite_cliques} onChange={(e) => setCamp({ ...camp, limite_cliques: e.target.value })} data-testid="camp-max-clicks" />
            </Campo>
            <Campo label="Frequência por utilizador (0 = automático)">
              <input className={input} type="number" min="0" value={camp.frequencia_utilizador} onChange={(e) => setCamp({ ...camp, frequencia_utilizador: e.target.value })} data-testid="camp-frequency" />
            </Campo>
            <div className="flex items-center gap-3 text-xs font-semibold text-slate-600 sm:col-span-2">
              {['desktop', 'tablet', 'mobile'].map((d) => (
                <label key={d} className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={camp[d]} onChange={(e) => setCamp({ ...camp, [d]: e.target.checked })} data-testid={`camp-${d}`} /> {d}
                </label>
              ))}
            </div>
            {camp.imagem_desktop && (
              <div className="sm:col-span-3" data-testid="camp-preview">
                <p className="text-[10px] font-bold uppercase text-slate-400">Pré-visualização</p>
                <img src={camp.imagem_desktop} alt="pré-visualização" className="mt-1 max-h-24 rounded-lg border border-slate-200" />
              </div>
            )}
            <div className="flex gap-2 sm:col-span-3">
              <button type="submit" disabled={busy} className={`${btn} bg-emerald-600 text-white disabled:opacity-60`} data-testid="camp-save">
                {campEdit ? 'Guardar campanha' : 'Criar campanha (rascunho)'}
              </button>
              {campEdit && <button type="button" onClick={() => { setCampEdit(null); setCamp(campVazia); }} className={`${btn} border border-slate-300 text-slate-600`} data-testid="camp-cancel">Cancelar</button>}
              <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (cancelar/apagar)" className={`${input} max-w-xs`} data-testid="camp-reason" />
            </div>
          </form>

          <p className="mt-2 text-[11px] text-slate-500" data-testid="camp-counters">
            {Object.entries(contadores).map(([k, v]) => `${k}: ${v}`).join(' · ')}
          </p>

          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-xs">
              <thead className="text-[10px] uppercase text-slate-400">
                <tr><th className="py-2">Campanha</th><th>Anunciante</th><th>Posição</th><th>Período</th><th>Estado</th><th>Impr.</th><th>Cliques</th><th>CTR</th><th /></tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`camp-row-${c.id}`}>
                    <td className="py-2">
                      <p className="font-semibold text-slate-800">{c.nome_interno}</p>
                      <p className="text-[10px] text-slate-400">prioridade {c.prioridade} · {Number(c.preco || 0).toFixed(2)} € + IVA</p>
                    </td>
                    <td>{c.anunciante}</td>
                    <td className="text-[10px]">{c.posicao}</td>
                    <td className="text-[10px]">{(c.inicio || '').slice(0, 10)} → {(c.fim || '').slice(0, 10)}</td>
                    <td><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase" data-testid={`camp-state-${c.id}`}>{c.estado}</span></td>
                    <td>{c.estatisticas?.impressoes ?? 0}</td>
                    <td>{c.estatisticas?.cliques ?? 0}</td>
                    <td>{c.estatisticas?.ctr ?? 0}%</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {['ativa', 'pausada', 'terminada', 'cancelada'].filter((s) => s !== c.estado).map((s) => (
                          <button key={s} type="button" disabled={busy}
                            onClick={() => acao(() => api.post(`${base}/campanhas/${c.id}/estado`, { estado: s, motivo }), `Campanha ${s}.`)}
                            className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`camp-to-${s}-${c.id}`}>{s}</button>
                        ))}
                        <button type="button" onClick={() => {
                          setCampEdit(c.id);
                          setCamp({ ...campVazia, ...c, inicio: (c.inicio || '').slice(0, 16), fim: (c.fim || '').slice(0, 16) });
                        }} className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`camp-edit-${c.id}`}>Editar</button>
                        <button type="button" disabled={busy}
                          onClick={() => window.confirm('Apagar esta campanha e as suas estatísticas?')
                            && acao(() => api.delete(`${base}/campanhas/${c.id}?motivo=${encodeURIComponent(motivo)}`), 'Campanha apagada.')}
                          className="rounded border border-rose-300 px-1.5 py-0.5 text-rose-700" data-testid={`camp-delete-${c.id}`}><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!campaigns.length && !loading && (
                  <tr><td colSpan={9} className="py-4 text-slate-500" data-testid="camp-empty">Sem campanhas.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'config' && (
        <div className="mt-3 grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-3" data-testid="admin-ads-config">
          <Campo label="Publicidade a cada N resultados">
            <input className={input} type="number" min="3" max="50" value={cfg.insert_every_n_items}
              onChange={(e) => setCfg({ ...cfg, insert_every_n_items: Number(e.target.value) })} data-testid="cfg-every" />
          </Campo>
          <Campo label="Primeira posição">
            <input className={input} type="number" min="1" max="50" value={cfg.primeira_posicao}
              onChange={(e) => setCfg({ ...cfg, primeira_posicao: Number(e.target.value) })} data-testid="cfg-first" />
          </Campo>
          <Campo label="Máximo por página">
            <input className={input} type="number" min="1" max="10" value={cfg.max_por_pagina}
              onChange={(e) => setCfg({ ...cfg, max_por_pagina: Number(e.target.value) })} data-testid="cfg-max" />
          </Campo>
          <div className="flex items-center gap-3 text-xs font-semibold text-slate-600 sm:col-span-2">
            {['desktop', 'tablet', 'mobile'].map((d) => (
              <label key={d} className="inline-flex items-center gap-1">
                <input type="checkbox" checked={!!cfg[d]} onChange={(e) => setCfg({ ...cfg, [d]: e.target.checked })} data-testid={`cfg-${d}`} /> {d}
              </label>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={() => acao(() => api.put(`${base}/config`, cfg), 'Configuração guardada.')}
            className={`${btn} bg-emerald-600 text-white disabled:opacity-60`} data-testid="cfg-save">Guardar configuração</button>
        </div>
      )}

      {tab === 'pedidos' && (
        <div className="mt-3 overflow-x-auto" data-testid="admin-ads-leads">
          <table className="w-full min-w-[700px] text-left text-xs">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr><th className="py-2">Contacto</th><th>Empresa</th><th>Categoria</th><th>Posições</th><th>Mensagem</th><th>Data</th></tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-slate-100" data-testid={`lead-row-${l.id}`}>
                  <td className="py-2"><p className="font-semibold text-slate-800">{l.nome}</p><p className="text-[10px] text-slate-400">{l.email} · {l.telefone || '—'}</p></td>
                  <td>{l.empresa || '—'}</td>
                  <td>{l.categoria}</td>
                  <td className="text-[10px]">{(l.posicoes || []).join(', ') || '—'}</td>
                  <td className="max-w-[220px] truncate text-[10px]">{l.mensagem || '—'}</td>
                  <td className="text-[10px]">{(l.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                </tr>
              ))}
              {!leads.length && !loading && (
                <tr><td colSpan={6} className="py-4 text-slate-500" data-testid="lead-empty">Sem pedidos de proposta.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
